/**
 * PropertiesPage Component
 * Lists all properties with filtering and creation — mobile-first.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTrackFeature } from '@resida/analytics';
import { Plus, Building } from 'lucide-react';
import {
  listProperties,
  listPortfolios,
  type PropertyWithStats,
  type PortfolioWithStats,
} from '../lib/api';
import { PageHeader, EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { PropertyTypeLabel } from '../lib/constants';

export function PropertiesPage() {
  const navigate = useNavigate();
  useTrackFeature('properties', 'landlord');
  const [searchParams] = useSearchParams();

  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [portfolios, setPortfolios] = useState<PortfolioWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const selectedPortfolioId = searchParams.get('portfolioId') || '';

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters = selectedPortfolioId ? { portfolioId: selectedPortfolioId } : undefined;
      const [propertiesRes, portfoliosRes] = await Promise.all([
        listProperties(filters),
        listPortfolios(),
      ]);

      setProperties(propertiesRes.properties);
      setPortfolios(portfoliosRes.portfolios);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load properties');
    } finally {
      setIsLoading(false);
    }
  }, [selectedPortfolioId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const portfolioMap = useMemo(() => {
    return new Map(portfolios.map((p) => [p.id, p]));
  }, [portfolios]);

  if (isLoading) {
    return <PropertiesPageSkeleton />;
  }

  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="properties-page">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Properties"
          subtitle={`${properties.length} propert${properties.length !== 1 ? 'ies' : 'y'}`}
          action={
            <Button
              onClick={() => navigate('/properties/new')}
              disabled={portfolios.length === 0}
              size="sm"
              data-testid="properties-add-button"
            >
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Add Property</span>
            </Button>
          }
        />
      </div>

      {error && (
        <div className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" role="alert">
          <p className="text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchData} className="mt-2">Retry</Button>
        </div>
      )}

      {/* Content */}
      {properties.length === 0 ? (
        <EmptyState
          icon={Building}
          title="No Properties Found"
          description={
            selectedPortfolioId
              ? 'No properties in this portfolio yet.'
              : 'Add your first property to get started.'
          }
          action={
            <Button
              onClick={() => navigate('/properties/new')}
              disabled={portfolios.length === 0}
              data-testid="properties-empty-add-button"
            >
              Add Property
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col">
          {properties.map((property) => {
            const navigationPath = property.propertyType === 'single_family' && property.defaultUnitId
              ? `/units/${property.defaultUnitId}`
              : `/properties/${property.id}`;
            const typeLabel = PropertyTypeLabel[property.propertyType] || property.propertyType;
            const portfolioName = portfolioMap.get(property.portfolioId)?.name;
            const unitSummary = `${property.unitCount ?? 0} unit${(property.unitCount ?? 0) !== 1 ? 's' : ''} · ${property.availableUnitCount ?? 0} available`;

            return (
              <button
                key={property.id}
                type="button"
                className="flex flex-col gap-1 px-[var(--page-padding-x)] py-3 min-h-[48px] border-b border-border last:border-b-0 hover:bg-accent transition-colors text-left w-full cursor-pointer"
                onClick={() => navigate(navigationPath)}
                data-testid={`property-item-${property.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground truncate">{property.name}</p>
                  <Badge variant="outline" className="shrink-0 text-xs">{typeLabel}</Badge>
                </div>
                {portfolioName && (
                  <p className="text-xs text-muted-foreground truncate">{portfolioName}</p>
                )}
                <p className="text-xs text-muted-foreground">{unitSummary}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PropertiesPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-6 w-36" />
        <Skeleton className="h-4 w-24 mt-2" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 space-y-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    </div>
  );
}

export default PropertiesPage;
